import React, { useState } from 'react'
import '../auth.css'
import { OTP_LENGTH, OTP_VALID_DURATION } from 'utils/constant'
import NavbarNew from 'components/navbar-new'
import { Container } from 'react-bootstrap'
import { useForm, Controller } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { useLocation, useNavigate } from 'react-router-dom'
import { loginSchema } from 'utils/yupSchema'
import OtpSection from 'components/auth/otp-section'
import { loginUser, verifyOtp } from '../../../redux/actions/authAction'
import { loginUserWithOtp } from '../../../redux/actions/userAction'
import { LOGIN, SIGNUP, STORIES } from 'routes/routeConstant'
import { LoginFormValues } from '../types'
import Footer from 'containers/layout/footer'
import { SET_AUTH_LOADER } from '../../../redux/types'
import { AppUseDispatch } from '../../../redux/hooks'
import { apiErrorMessage } from 'i18n/apiError'

const Login = () => {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const location = useLocation()
  const from: any = (location.state as any)?.from
  const dispatch = AppUseDispatch()
  const [showOtpSection, setShowOtpSection] = useState(false)
  const [userData, setUserData] = useState<LoginFormValues | null>(null)
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)
  const [userNotFound, setUserNotFound] = useState<string | null>(null)
  const [isHidden, setIsHidden] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: yupResolver(loginSchema),
    mode: 'onSubmit',
  })

  // OTP state
  const [otp, setOtp] = useState('')
  const [timer, setTimer] = useState(OTP_VALID_DURATION)
  const [otpError, setOtpError] = useState<string | null>(null)
  // const [otpSuccess, setOtpSuccess] = useState<string | null>(null)

  const handleFormSubmit = async (data: LoginFormValues) => {
    setLoading(true)
    try {
      await loginUser(data).then((res: any) => {
        if (res?.status === 429) {
          setApiError(apiErrorMessage(res, 'auth:login.errors.loginFailed'))
          setUserNotFound(null)
        }
        if (res?.status === 404) {
          setUserNotFound(apiErrorMessage(res, 'auth:login.errors.loginFailed'))
          setApiError(null)
        }
        if (res?.status === 400) {
          setUserData(data)
          setApiError(apiErrorMessage(res, 'auth:login.errors.loginFailed'))
        } else {
          if (res?.status === 200) {
            setUserData(data)
            setShowOtpSection(true)
          }
        }
      })
    } catch (err: any) {
      setApiError(apiErrorMessage(err, 'auth:login.errors.loginFailed'))
      setUserNotFound(null)
    } finally {
      setLoading(false)
    }
  }

  const handleBackClick = () => {
    setShowOtpSection(false)
    setOtp('')
    setTimer(OTP_VALID_DURATION)
    setOtpError(null)
    // setOtpSuccess(null)
    setApiError(null)
    setIsHidden(false)
  }

  React.useEffect(() => {
    if (!showOtpSection || timer <= 0) return
    const interval = setInterval(() => setTimer((t) => t - 1), 1000)
    return () => clearInterval(interval)
  }, [showOtpSection, timer])

  const handleResend = async () => {
    setTimer(OTP_VALID_DURATION)
    setOtp('')
    setOtpError(null)
    setIsHidden(true)
    // setOtpSuccess(null)
    try {
      await loginUser(userData).then((res: any) => {
        if (res?.status === 429) {
          setOtp('')
          setTimer(0)
          setApiError(apiErrorMessage(res, 'auth:login.errors.resendFailed'))
          setIsHidden(true)
        }
      })
    } catch (err: any) {
      setApiError(apiErrorMessage(err, 'auth:login.errors.resendFailed'))
      setIsHidden(true)
    }
  }

  const handleOtpSubmit = async () => {
    setOtpError(null)

    if (!userData?.email || otp.length !== OTP_LENGTH) {
      setOtpError(t('login.errors.otpInvalidFormat'))
      return
    }

    try {
      const response = await verifyOtp({
        email: userData.email,
        otp,
      })

      if (response.status === 200 && response?.data) {
        loginUserWithOtp({ userDetails: response?.data || null })

        const redirectTo =
          from && (from.pathname || typeof from === 'string') && (from.pathname !== LOGIN && from !== LOGIN)
            ? from
            : STORIES

        try {
          setTimeout(() => {
            dispatch({ type: SET_AUTH_LOADER, payload: false })
            navigate(redirectTo, { replace: true })
          }, 1000)
        } catch (storyErr) {
          setOtpError(t('login.errors.storiesFailed'))
          return
        }
      } else {
        const errorMessage = apiErrorMessage(response, 'auth:login.errors.otpFailed')
        dispatch({ type: SET_AUTH_LOADER, payload: false })
        setOtpError(errorMessage)
        if (response.status === 429) {
          setOtp('')
          setTimer(0)
          setIsHidden(true)
          setApiError(apiErrorMessage(response, 'auth:login.errors.tooManyRequests'))
          return
        } else if (response.status === 400) {
          setOtp('')
          setTimer(0)
          setIsHidden(false)
          setApiError(apiErrorMessage(response, 'auth:login.errors.otpInvalid'))
          return
        }
      }
    } catch (err: any) {
      dispatch({ type: SET_AUTH_LOADER, payload: false })
      setOtpError(apiErrorMessage(err, 'auth:login.errors.generic'))
    }
  }

  return (
    <>
      <div className='auth_bg_wrapper'>
        <div className='gradient1'>
          <img src='images/banner-gradient-img.svg' alt='' />
        </div>
        <div className='gradient2'>
          <img src='images/banner-gradient-img2.svg' alt='' />
        </div>
        <div className='gradient3'>
          <img src='images/banner-gradient-img3.svg' alt='' />
        </div>

        <NavbarNew />
        <Container className='middle_contains_div'>
          <div className='auth-page-wrapper'>
            {!showOtpSection && (
              <div className='auth-modal-box'>
                <div className='auth-header-div'>
                  <h2>{t('login.title')}</h2>
                  <p>{t('login.subtitle')}</p>
                </div>
                <form className='form-wrapper-main-div' onSubmit={handleSubmit(handleFormSubmit)}>
                  <div className='auth-input-group-div'>
                    <div className='form-group auth-input-group'>
                      <Controller
                        name='email'
                        control={control}
                        defaultValue=''
                        render={({ field }) => (
                          <input
                            type='email'
                            className={`form-input-2 w-input${errors.email ? ' is-invalid' : ''}`}
                            placeholder={t('login.emailPlaceholder')}
                            autoComplete='email'
                            {...field}
                            onChange={(e) => {
                              field.onChange(e)
                            }}
                          />
                        )}
                      />
                      {errors && (
                        <div className='error-text'>{errors?.email?.message ? t(errors.email.message) : undefined}</div>
                      )}
                      {apiError && <div className='error-text'>{apiError}</div>}
                      {userNotFound && (
                        <div className='error-text'>
                          <Trans
                            t={t}
                            i18nKey='login.userNotFound'
                            values={{ message: userNotFound }}
                            components={[<a key='signup' className='not-found-text' href={`${SIGNUP}`} />]}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='auth-button-div'>
                    <button type='submit' className='button-2 green-btn-class w-button w-100' disabled={loading}>
                      {loading ? t('pleaseWait') : t('login.submit')}
                    </button>
                  </div>
                </form>
              </div>
            )}
            {showOtpSection && (
              <div className='auth-modal-box'>
                <OtpSection
                  otp={otp}
                  setOtp={setOtp}
                  setOtpError={setOtpError}
                  setIsHidden={setIsHidden}
                  onClick={handleOtpSubmit}
                  btnTitle={t('login.otpSubmit')}
                  handleBackClick={handleBackClick}
                  timer={timer}
                  handleResend={handleResend}
                  email={userData?.email}
                  otpError={otpError}
                  isHidden={isHidden}
                />
                {/* {otpError && (
                  <div className='otp-sec-text' style={{ color: 'red', marginTop: 8 }}>
                    {otpError}
                  </div>
                )} */}
                {/* {otpSuccess && <div className='otp-success-text'>{otpSuccess}</div>} */}
              </div>
            )}
          </div>
        </Container>
        <Footer />
      </div>
    </>
  )
}

export default Login
